<script setup lang="ts">
import SmartIcon from './SmartIcon.vue';

export interface AlertAction {
  text: string;
  onClick?: () => Promise<void> | void | 'dismiss';
}

interface Props {
  title?: string;
  message?: string;
  type?: 'success' | 'error' | 'warning' | 'info' | 'sparkles';
  actions?: AlertAction[];
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  message: undefined,
  type: 'info',
  actions: undefined,
});

const slots = defineSlots<{
  title(): void;
  message(): void;
}>();

const icon = computed(() => {
  switch (props.type) {
    case 'success':
      return { name: 'heroicons:check-circle' };
    case 'error':
      return { name: 'heroicons:x-circle' };
    case 'info':
      return { name: 'heroicons:information-circle' };
    case 'warning':
      return { name: 'heroicons:exclamation-triangle' };
    case 'sparkles':
      return { name: 'heroicons:sparkles' };
    default:
      return { name: 'heroicons:information-circle' };
  }
});

const hasTitle = computed(() => !!props.title || !!slots.title);
</script>

<template>
  <div
    :class="{
      'border-system-success-green bg-system-success-light': type === 'success',
      'border-system-error-red bg-system-error-light': type === 'error',
      'border-brand-primary bg-brand-primary-soft': type === 'info',
      'border-system-warning-yellow bg-system-warning-light': type === 'warning',
      'border-brand-secondary bg-brand-secondary/10': type === 'sparkles',
      'border-l-4 !py-6': hasTitle,
      'md:!min-w-[450px]': hasTitle,
    }"
    class="flex w-full flex-nowrap gap-2 rounded-lg px-3 py-2 md:min-w-[350px]"
  >
    <div
      data-testid="icon-container"
      class="hidden items-start justify-center md:flex"
      :class="{ 'mx-3': hasTitle }"
    >
      <span
        :class="{
          '!bg-transparent': !hasTitle,
          'h-full': !hasTitle,
          'bg-system-warning-yellow': type === 'warning',
          'bg-system-success-green': type === 'success',
          'bg-system-error-red': type === 'error',
          'bg-brand-primary': type === 'info',
          'bg-transparent': type === 'sparkles',
        }"
        class="flex items-center justify-center rounded-md p-1"
      >
        <IconifyIcon
          :icon="icon.name"
          v-if="type !== 'sparkles'"
          class="size-5"
          :class="{
            '!text-white': hasTitle,
            'text-system-warning-dark': type === 'warning',
            'text-system-error-dark': type === 'error',
            'text-brand-primary-dark': type === 'info',
            'text-system-success-dark': type === 'success',
          }"
        ></IconifyIcon>
        <SmartIcon v-if="type === 'sparkles'" class="size-5" />
      </span>
    </div>
    <div class="flex w-full flex-col justify-center" :class="{ 'w-11/12': hasTitle }">
      <div
        :class="{
          'text-system-warning-dark': type === 'warning',
          'text-system-error-dark': type === 'error',
          'text-brand-primary-dark': type === 'info',
          'text-system-success-dark': type === 'success',
          'text-brand-primary': type === 'sparkles',
        }"
        v-if="hasTitle"
        class="mb-2 text-lg font-medium leading-4"
      >
        <slot name="title">
          <h3 v-if="hasTitle">
            {{ title }}
          </h3>
        </slot>
      </div>
      <div
        :class="{
          'text-system-warning-dark/70': type === 'warning' && title,
          'text-system-error-dark/70': type === 'error' && title,
          'text-brand-primary-dark/70': type === 'info' && title,
          'text-system-success-dark/70': type === 'success' && title,
          'text-system-warning-dark': type === 'warning' && !title,
          'text-system-error-dark': type === 'error' && !title,
          'text-brand-primary-dark': type === 'info' && !title,
          'text-system-success-dark': type === 'success' && !title,
          'text-brand-primary': type === 'sparkles' && !title,
        }"
        class="flex w-full items-center justify-between text-sm"
      >
        <slot name="message">
          <p>
            {{ message }}
          </p>
        </slot>
        <div v-if="actions && !hasTitle" class="flex gap-3">
          <button
            v-for="action in actions"
            :key="action.text"
            type="button"
            class="flex items-center gap-1 text-sm font-medium underline-offset-2 hover:underline"
            :class="{
              'text-system-warning-dark': type === 'warning',
              'text-system-error-dark': type === 'error',
              'text-brand-primary-dark': type === 'info',
              'text-system-success-dark': type === 'success',
              'text-brand-primary': type === 'sparkles',
            }"
            @click="action.onClick"
          >
            <span>{{ action.text }}</span>
          </button>
        </div>
      </div>
      <div v-if="actions && hasTitle" class="mt-2 flex gap-3">
        <button
          v-for="action in actions"
          :key="action.text"
          type="button"
          class="flex items-center gap-1 text-sm font-medium underline-offset-2 hover:underline"
          :class="{
            'text-system-warning-dark': type === 'warning',
            'text-system-error-dark': type === 'error',
            'text-brand-primary-dark': type === 'info',
            'text-system-success-dark': type === 'success',
            'text-brand-primary': type === 'sparkles',
          }"
          @click="action.onClick"
        >
          <span>{{ action.text }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
